import { SDKResponse } from '@commercetools/frontend-sdk';
import { WithSDKError } from 'shared/types/shared';
import { SDKRetryOptions } from './types';

const CT_INFRA_ERROR_MESSAGES = [
  'Failed to fetch',
  'Load failed',
  'Temporary overloading',
  'NetworkError',
  'temporary network error',
  "Oops. This shouldn't happen.",
  'socket hang up',
  'SQLSTATE',
  'upstream connect error or disconnect/reset before headers',
  'FetchEvent.respondWith received an error',
  'Back-end server is at capacity',
  'Retry later',
];

/**
 * Makes a Commercetools API call with retry logic in case of failure
 *
 * This function makes an API call with a total of 4 attempts.
 * One initial call and up to 3 retries if the call fails.
 * Each retry is separated by an linearly increasing backoff period,
 * starting at 1000ms and increasing by 1000ms with each subsequent attempt.
 */
export const makeSDKCallWithRetries = async <T extends WithSDKError>(
  actionName: string,
  customerId: string,
  fn: () => Promise<SDKResponse<T>>,
  {
    totalRetries = 3,
    baseBackoff = 1000,
    retryErrorCodes = [],
    retryErrorMessages = [],
  }: SDKRetryOptions = {},
): Promise<SDKResponse<T>> => {
  const attempt = async (attemptNumber: number): Promise<SDKResponse<T>> => {
    const response = await fn();

    const error = response.isError
      ? response.error
      : response.data.isError
        ? response.data.error
        : null;
    const errorCode = (error?.code ?? error?.name) as string;

    const hasCtInfraError =
      !!error &&
      CT_INFRA_ERROR_MESSAGES.some((message) =>
        error.message?.includes(message),
      );

    const hasRetryErrorForAction =
      !!error &&
      (retryErrorCodes.includes(errorCode) ||
        retryErrorMessages.some((regex) => regex.test(error.message)));

    if (!hasCtInfraError && !hasRetryErrorForAction) {
      return response;
    }

    const nextAttempt = attemptNumber + 1;
    const nextBackoff = baseBackoff * nextAttempt;

    if (attemptNumber >= totalRetries) {
      console.error(
        `Exceeded retries attempt for ${actionName} for customer: ${customerId} with error:`,
        error,
      );
      return response;
    }

    console.warn(
      `${actionName} for customer: ${customerId} failed with error: ${errorCode}: ${error.message}. Retrying attempt #${nextAttempt} in ${nextBackoff} ms...`,
    );

    await new Promise((res) => {
      setTimeout(res, nextBackoff);
    });

    return attempt(nextAttempt);
  };

  return attempt(0);
};
